/* eslint-disable react/prop-types */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import {
  getLinkFavorites,
  addLinkFavorite,
  removeLinkFavorite,
  getLinkVisits,
  recordLinkVisit,
} from '../apiServices/links/links';
import { useProfile } from './ProfileProvider';

const LinksContext = createContext();

export function LinksProvider({ children }) {
  const { profile } = useProfile();
  const workspaceName = profile?.config?.currentWorkspace;
  const [favorites, setFavorites] = useState([]);
  const [visits, setVisits] = useState([]);

  const refreshFavorites = useCallback(async () => {
    if (!workspaceName) {
      setFavorites([]);
      return;
    }
    try {
      const res = await getLinkFavorites({ workspaceName });
      setFavorites(res.data?.linkFavorites || []);
    } catch {
      // Best-effort: favorites/history are a convenience feature, never
      // block navigation if the call fails.
    }
  }, [workspaceName]);

  const refreshVisits = useCallback(async () => {
    if (!workspaceName) {
      setVisits([]);
      return;
    }
    try {
      const res = await getLinkVisits({ workspaceName });
      setVisits(res.data?.linkVisits || []);
    } catch {
      // See refreshFavorites above.
    }
  }, [workspaceName]);

  useEffect(() => {
    refreshFavorites();
    refreshVisits();
  }, [refreshFavorites, refreshVisits]);

  const isFavorite = useCallback(
    (url) => favorites.some((favorite) => favorite.url === url),
    [favorites],
  );

  const toggleFavorite = useCallback(async (url, label) => {
    if (!workspaceName) return;
    if (isFavorite(url)) {
      await removeLinkFavorite({ workspaceName, url });
    } else {
      await addLinkFavorite({ workspaceName, url, label });
    }
    refreshFavorites();
  }, [workspaceName, isFavorite, refreshFavorites]);

  const trackVisit = useCallback(async (url, label) => {
    if (!workspaceName) return;
    await recordLinkVisit({ workspaceName, url, label });
    refreshVisits();
  }, [workspaceName, refreshVisits]);

  const contextValue = useMemo(() => ({
    favorites,
    visits,
    isFavorite,
    toggleFavorite,
    trackVisit,
  }), [favorites, visits, isFavorite, toggleFavorite, trackVisit]);

  return (
    <LinksContext.Provider value={contextValue}>
      {children}
    </LinksContext.Provider>
  );
}

export const useLinks = () => useContext(LinksContext);
